<template>
  <invite-view
    :invite="invite"
    :from-profile="fromProfile"
    :recipe-title="recipeTitle"
    :loading="loading"
    :error="error"
    @accept="accept"
    @decline="decline"
  />
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue';
  import { useFirestore } from 'vuefire';
  import { useAuthStore } from '@/stores/auth';
  import { useRouter } from 'vue-router';
  import InviteClient from '@/service/invite/inviteClient';
  import GroupService from '@/service/group/groupService';
  import InviteView from '@/views/invite/InviteView.vue';
  import type { Invite } from '@/service/invite/types';
  import type { Profile } from '@/service/profile/types';
  import { doc, getDoc } from 'firebase/firestore';

  export default defineComponent({
    name: 'InviteViewRoute',
    components: { InviteView },
    props: {
      id: {
        type: String,
        required: true,
      },
    },
    async setup(props) {
      const firestore = useFirestore();
      const authStore = useAuthStore();
      const router = useRouter();
      const uid = authStore.getIdentifier().uid;
      const inviteClient = new InviteClient(firestore, uid);

      const invite = ref<Invite | undefined>(undefined);
      const fromProfile = ref<Profile | undefined>(undefined);
      const recipeTitle = ref<string | undefined>(undefined);
      const loading = ref(false);
      const error = ref<string | undefined>(undefined);

      const fetched = await inviteClient.getInvite(props.id);
      if (!fetched) {
        error.value = 'This invite link is no longer valid.';
      } else if (fetched.fromUid === uid) {
        error.value = 'You cannot accept your own invite.';
      } else {
        invite.value = fetched;
        const groupService = new GroupService(firestore, uid);
        const profiles = await groupService.getProfilesByUids([
          fetched.fromUid,
        ]);
        if (profiles.length) {
          fromProfile.value = profiles[0];
        }
        if (fetched.type === 'recipe' && fetched.recipeId) {
          const recipeDoc = await getDoc(
            doc(firestore, 'recipes', fetched.recipeId),
          );
          if (recipeDoc.exists()) {
            recipeTitle.value = recipeDoc.data().title;
          }
        }
      }

      return {
        invite,
        fromProfile,
        recipeTitle,
        loading,
        error,
        async accept() {
          if (!invite.value) return;
          loading.value = true;
          try {
            if (invite.value.type === 'family') {
              await inviteClient.acceptFamilyInvite(invite.value);
              window.location.href = '/';
            } else {
              await inviteClient.acceptRecipeInvite(invite.value);
              await router.push({ name: 'list' });
            }
          } catch {
            error.value = 'Something went wrong accepting the invite.';
            loading.value = false;
          }
        },
        async decline() {
          await router.push({ name: 'list' });
        },
      };
    },
  });
</script>
